import React, { useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { Container, Row } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import { AuthProvider, useAuth } from "./authContext";

import "react-toastify/dist/ReactToastify.css";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";
import * as Sentry from "@sentry/browser";
import { config } from "./CookieConsentConfig";
import "./custom.scss";
import "./globalFontsAndVars.css";

import LoginPage from "./components/pages/LoginPage/LoginPage";
import MaturaApplicationPage from "./components/pages/MaturaApplicationPage/MaturaApplicationPage";
import MaturaApplicationSentPage from "./components/pages/MaturaApplicationSentPage/MaturaApplicationSentPage";
import SideBar from "./components/SideBar/SideBar";
import RootComponent from "./components/RootComponent/RootComponent";
import MobileNavbar from "./components/MobileNavbar/MobileNavbar";
import AboutPage from "./components/pages/AboutPage/AboutPage";
import HomePagePreparations from "./components/pages/HomePage/HomePagePreparations";
import SubjectsPage from "./components/pages/SubjectsPage/SubjectsPage";
import TeacherAdminListPage from "./components/pages/TeachersAdmin/TeacherAdminListPage/TeacherAdminListPage";
import TeacherAddEditPage from "./components/pages/TeachersAdmin/TeacherAddEditPage/TeacherAddEditPage";
import CourseAddEditPage from "./components/pages/Coursees/CourseAddPage/CourseAddEditPage";
import CoursesAdminListPage from "./components/pages/Coursees/CoursesAdminListPage/CoursesAdminListPage";
import CoursePage from "./components/pages/Coursees/CoursePage/CoursePage";
import MaturaApplicaitonsListPage from "./components/pages/MaturaApplicationsListPage/MaturaApplicaitonsListPage";
import DashboardPage from "./components/pages/DashboardPage/DashboardPage";
import PasswordSetupPage from "./components/pages/PasswordSetupPage/PasswordSetupPage";
import PricePage from "./components/pages/PricePage/PricePage";
import PodukePricePage from "./components/pages/PodukePricePage/PodukePricePage";
import PrivacyPolicyPage from "./components/pages/PrivacyPolicyPage/PrivacyPolicyPage";
import CookiePolicyPage from "./components/pages/CookiePolicyPage/CookiePolicyPage";
import Footer from "./components/Footer/Footer";
import TermsAndConditionsPage from "./components/pages/TermsAndConditionsPage/TermsAndConditionsPage";
import OrderPayment from "./components/pages/Payments/OrderPayments";
import CreditCardSecurity from "./components/pages/CreditCardSecurity/CreditCardSecurity";
import PaymentSuccessful from "./components/pages/Payments/PaymentSuccessful/PaymentSuccessful";
import PaymentInProgress from "./components/pages/Payments/PaymentInProgress/PaymentInProgress";
import PrivacyStatementPage from "./components/pages/PrivacyStatementPage/PrivacyStatementPage";
import ForgotPasswordInitial from "./components/pages/ForgotPassword/ForgotPasswordInitial";
import ForgotPasswordReset from "./components/pages/ForgotPassword/ForgotPasswordReset";
import { SubjectKind } from "./common/types";
import HomePageLessons from "./components/pages/HomePage/HomePageLessons";
import HomePage from "./components/pages/HomePage/HomePage";
import TeacherPage from "./components/pages/TeacherPage/TeacherPage";
import CalendarPage from "./components/pages/CalendarPage/CalendarPage";
import LessonsReservationPage from "./components/pages/LessonsReservationPage/LessonsReservationPage";
import ScrollToTop from "./components/Helpers/ScrollToTop";
import NewHomePage from "./components/pages/NewHomePage/NewHomePage";
import NewAboutPage from "./components/pages/NewAboutPage/NewAboutPage";
import NewPreparations from "./components/pages/NewPreparationsPage/NewPreparations";
import NewLessonsPage from "./components/pages/NewLessonsPage/NewLessonsPage";
import NewTeacherPage from "./components/pages/NewTeachersPage/NewTeacherPage";
import NewPricePage from "./components/pages/NewPricePage/NewPricePage";
import NewLoginPage from "./components/pages/NewLoginPage/NewLoginPage";
import NewMaturaCroatianEssayApplicationPage from "./components/pages/new/NewMaturaCroatianEssayApplicationPage";
import NewLessonsReservationPage from "./components/pages/NewLessonsReservationPage/NewLessonsReservationPage";

const RequireRouteAccess = ({ allowedAccess }: { allowedAccess: string[] }) => {
  const { userProfile } = useAuth();
  const location = useLocation();
  return userProfile && allowedAccess.includes(userProfile.role) ? (
    <Outlet />
  ) : (
    <Navigate to="/" state={{ from: location }} replace />
  );
};

const AppRoutes = () => (
  <Routes>
    {/* New main routes */}
    <Route path="/" element={<NewHomePage />} />
    <Route path="/pripreme-za-maturu" element={<NewPreparations />} />
    <Route path="/poduke" element={<NewLessonsPage />} />
    <Route path="/cjenik" element={<NewPricePage />} />
    <Route path="/poduke/cjenik" element={<Navigate to="/cjenik" replace />} />
    <Route path="/prijava" element={<NewLoginPage />} />
    <Route path="/zaboravljena-lozinka" element={<ForgotPasswordInitial />} />
    <Route
      path="/reset-lozinke/:uid/:token"
      element={<ForgotPasswordReset />}
    />
    <Route
      path="/prijava-pripreme-drzavna-matura"
      element={<NewMaturaCroatianEssayApplicationPage />}
    />
    <Route
      path="/prijava-uspjesno-poslana"
      element={<MaturaApplicationSentPage />}
    />
    <Route path="/nastavnici" element={<NewTeacherPage />} />
    <Route
      path="/poduke/nastavnici"
      element={<TeacherPage kind={SubjectKind.LESSON} />}
    />
    <Route path="/o-nama" element={<NewAboutPage />} />
    <Route path="/grupa/:id" element={<CoursePage />} />

    <Route path="/dashboard" element={<DashboardPage />} />

    <Route path="/poduke/rezervacija" element={<NewLessonsReservationPage />} />

    {/* Old routes kept for backward compatibility */}
    <Route path="/old" element={<HomePage />} />
    <Route path="/old/pripreme-za-maturu" element={<HomePagePreparations />} />
    <Route path="/old/poduke" element={<HomePageLessons />} />
    <Route path="/old/cjenik" element={<PricePage />} />
    <Route path="/old/prijava" element={<LoginPage />} />
    <Route
      path="/old/prijava-pripreme-drzavna-matura"
      element={<MaturaApplicationPage />}
    />
    <Route
      path="/old/nastavnici"
      element={<TeacherPage kind={SubjectKind.PREPARATION} />}
    />
    <Route path="/old/o-nama" element={<AboutPage />} />
    <Route
      path="/old/poduke/rezervacija"
      element={<LessonsReservationPage />}
    />

    <Route path="/izjava-privatnosti" element={<PrivacyStatementPage />} />
    <Route path="/politika-privatnosti" element={<PrivacyPolicyPage />} />
    <Route path="/cookie-policy" element={<CookiePolicyPage />} />
    <Route path="/opci-uvjeti" element={<TermsAndConditionsPage />} />
    <Route path="sigurnost-placanja" element={<CreditCardSecurity />} />
    <Route path="/placanje-u-tijeku" element={<PaymentInProgress />} />
    <Route path="/uspjesno-placanje" element={<PaymentSuccessful />} />

    <Route path="/accounts/activate" element={<PasswordSetupPage />} />
    <Route path="/narudzba/:id" element={<OrderPayment />} />

    <Route path="/accounts/activate" element={<PasswordSetupPage />} />

    <Route
      element={<RequireRouteAccess allowedAccess={["CLERK", "PRINCIPAL"]} />}
    >
      <Route path="/prijave" element={<MaturaApplicaitonsListPage />} />
    </Route>
    <Route
      element={<RequireRouteAccess allowedAccess={["CLERK", "PRINCIPAL"]} />}
    >
      <Route
        path="/predmeti"
        element={<SubjectsPage kind={SubjectKind.PREPARATION} />}
      />
    </Route>
    <Route
      element={<RequireRouteAccess allowedAccess={["CLERK", "PRINCIPAL"]} />}
    >
      <Route
        path="/nastavnici-admin"
        element={<TeacherAdminListPage kind={SubjectKind.PREPARATION} />}
      />
    </Route>

    <Route
      element={<RequireRouteAccess allowedAccess={["CLERK", "PRINCIPAL"]} />}
    >
      <Route
        path="/dodaj-nastavnika"
        element={<TeacherAddEditPage kind={SubjectKind.PREPARATION} />}
      />
    </Route>
    <Route
      element={
        <RequireRouteAccess allowedAccess={["ACCOUNTANT", "PRINCIPAL"]} />
      }
    >
      <Route
        path="/poduke/predmeti"
        element={<SubjectsPage kind={SubjectKind.LESSON} />}
      />
      <Route
        path="/poduke/dodaj-nastavnika"
        element={<TeacherAddEditPage kind={SubjectKind.LESSON} />}
      />
      <Route
        path="/poduke/nastavnici-admin/:id"
        element={<TeacherAddEditPage kind={SubjectKind.LESSON} />}
      />
      <Route
        path="/poduke/nastavnici-admin"
        element={<TeacherAdminListPage kind={SubjectKind.LESSON} />}
      />
    </Route>
    <Route
      element={
        <RequireRouteAccess
          allowedAccess={["ACCOUNTANT", "PRINCIPAL", "TEACHER"]}
        />
      }
    >
      <Route path="/poduke/kalendar" element={<CalendarPage />} />
    </Route>

    <Route
      element={<RequireRouteAccess allowedAccess={["CLERK", "PRINCIPAL"]} />}
    >
      <Route
        path="/nastavnici-admin/:id"
        element={<TeacherAddEditPage kind={SubjectKind.PREPARATION} />}
      />
    </Route>
    <Route
      element={<RequireRouteAccess allowedAccess={["CLERK", "PRINCIPAL"]} />}
    >
      <Route path="/dodaj-grupu" element={<CourseAddEditPage />} />
    </Route>
    <Route
      element={<RequireRouteAccess allowedAccess={["CLERK", "PRINCIPAL"]} />}
    >
      <Route path="/grupe-admin" element={<CoursesAdminListPage />} />
      <Route
        path="/grupe-admin/edit/:courseId?"
        element={<CourseAddEditPage />}
      />
    </Route>
  </Routes>
);

function AppShell() {
  const location = useLocation();
  const hideChromePaths = [
    "/",
    "/o-nama",
    "/pripreme-za-maturu",
    "/poduke",
    "/poduke/rezervacija",
    "/nastavnici",
    "/cjenik",
    "/prijava",
    "/prijava-pripreme-drzavna-matura",
    "/izjava-privatnosti",
    "/politika-privatnosti",
    "/cookie-policy",
    "/opci-uvjeti",
    "/sigurnost-placanja",
    "/prijava-uspjesno-poslana",
  ];
  const hideChrome = hideChromePaths.includes(location.pathname);

  return (
    <>
      {!hideChrome && <SideBar />}
      <div
        id="wrapper"
        style={{
          overflow: "auto",
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ScrollToTop />
        <div style={{ flex: 1 }}>
          {!hideChrome && <MobileNavbar />}
          {!hideChrome ? (
            <Container>
              <Row className="justify-content mt-3">
                <AppRoutes />
              </Row>
            </Container>
          ) : (
            <AppRoutes />
          )}
        </div>
        {!hideChrome && <Footer />}
      </div>
    </>
  );
}

function App() {
  useEffect(() => {
    CookieConsent.run(config);

    if (process.env.NODE_ENV !== "development") {
      Sentry.init({
        dsn: "https://5d302a4a8bf0b2b6e1aa8240cd809bf2@o4509032578416640.ingest.de.sentry.io/4509032581169232",
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1, // Capture replays of 100% of errors
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            networkDetailAllowUrls: [/.*/],
          }),
        ],
        // Tracing
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["localhost", /lumenathome\.com/],
      });
    }
  }, []);

  return (
    <AuthProvider>
      <RootComponent>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
        <ToastContainer />
      </RootComponent>
    </AuthProvider>
  );
}

export default App;
